<template>
  <div :class="{ horizontal: horizontalLayout }">
    <template v-if="sender.thumbUrl">
      <div v-if="imageTransforms" ref="containerRef" class="image-container profile-picture">
        <div :style="insideRefStyle" class="image" />
      </div>
      <img v-else :src="sender.thumbUrl" class="image profile-picture" />
    </template>
    <FriedAvatar
      v-else
      :name="AvatarHelper.getDisplayName(sender)"
      class="sender-initials profile-picture"
    />
  </div>
</template>
<script lang="ts" setup>
import type { Participant } from '@getaccept/dsr-shared';
import { AvatarHelper } from '@getaccept/dsr-shared';
import { ImageTransformHelper } from '@getaccept/editor-lib';
import { computed, onMounted, ref } from 'vue';

const { sender, imageTransforms } = defineProps<{
  sender: Participant;
  imageTransforms?: {
    scale: number;
    translateX: number;
    translateY: number;
  };
  horizontalLayout?: boolean;
}>();

const scale = computed(() => imageTransforms?.scale || ImageTransformHelper.SCALE_MIN_VALUE);
const ratio = computed(() => scale.value / 100);

const translateX = computed(() => imageTransforms?.translateX || 0);
const translateY = computed(() => imageTransforms?.translateY || 0);

const imageLoadElement = ref<HTMLImageElement>(null);
const containerRef = ref<HTMLDivElement>(null);

const initialSize = computed(() => {
  if (!containerRef.value || !imageLoadElement.value) {
    return;
  }
  const { clientWidth, clientHeight } = containerRef.value;
  const { naturalWidth, naturalHeight } = imageLoadElement.value;

  const [initialWidth, initialHeight] = ImageTransformHelper.getInitialImageSize(
    naturalWidth,
    naturalHeight,
    clientWidth,
    clientHeight
  );

  return { initialWidth, initialHeight };
});

const transformOrigin = computed(() => {
  if (!containerRef.value || !initialSize.value) {
    return;
  }
  const { initialWidth, initialHeight } = initialSize.value;
  const { clientWidth, clientHeight } = containerRef.value;

  return ImageTransformHelper.getTransformOrigin(
    initialHeight * ratio.value,
    initialWidth * ratio.value,
    clientHeight,
    clientWidth
  );
});

const insideRefStyle = computed(() => ({
  width: `${initialSize.value?.initialWidth}px`,
  height: `${initialSize.value?.initialHeight}px`,
  transform: `scale(${ratio.value}) translateX(${translateX.value / ratio.value}%) translateY(${translateY.value / ratio.value}%) translateZ(0)`,
  backgroundImage: `url(${sender.thumbUrl})`,
  transformOrigin: transformOrigin.value,
}));

onMounted(() => {
  imageLoadElement.value = new Image();
  imageLoadElement.value.src = sender.thumbUrl;
});
</script>
<style lang="scss" scoped>
img,
.sender-initials.avatar.medium {
  width: 100%;
  height: auto;
}

.sender-initials.avatar.medium {
  aspect-ratio: 1;
}

.image-container {
  overflow: hidden;
}

.profile-picture,
.profile-picture.avatar {
  border-radius: var(--radius-medium) var(--radius-medium) 0 0;
}

.horizontal {
  display: flex;

  .profile-picture,
  .profile-picture.avatar {
    border-radius: var(--radius-small) 0 0 var(--radius-small);
  }

  .image,
  .sender-initials.avatar.medium {
    width: auto;
    height: 100%;
  }
}
</style>
